import { Navigate, useParams } from 'react-router';

import { useSelector } from 'src/state';
import { selectWorkspaces, selectCurrentWorkspace } from 'src/state/workspace/selectors';

import { paths } from '../paths';

// ----------------------------------------------------------------------

/**
 * Redirects from legacy content routes to new workspace-based routes
 */
export function LegacyContentRedirect() {
  const currentWorkspace = useSelector(selectCurrentWorkspace);

  if (!currentWorkspace) {
    return <Navigate to={paths.dashboard.root} replace />;
  }

  return <Navigate to={paths.dashboard.content(currentWorkspace.id, 'root')} replace />;
}

/**
 * Redirects from legacy content editor routes to new workspace-based routes
 */
export function LegacyContentEditorRedirect() {
  const { contentId } = useParams<{ contentId: string }>();
  const currentWorkspace = useSelector(selectCurrentWorkspace);

  if (!currentWorkspace || !contentId) {
    return <Navigate to={paths.dashboard.root} replace />;
  }

  return (
    <Navigate to={paths.dashboard.contentEditor(currentWorkspace.id, 'root', contentId)} replace />
  );
}

/**
 * Redirects from legacy worksheet routes to new workspace-based routes
 */
export function LegacyWorksheetRedirect() {
  const { worksheetId } = useParams<{ worksheetId: string }>();
  const currentWorkspace = useSelector(selectCurrentWorkspace);

  if (!currentWorkspace || !worksheetId) {
    return <Navigate to={paths.dashboard.root} replace />;
  }

  return <Navigate to={paths.dashboard.worksheet(currentWorkspace.id, worksheetId)} replace />;
}

/**
 * Redirects from legacy worksheet test route to new workspace-based route
 */
export function LegacyWorksheetTestRedirect() {
  const currentWorkspace = useSelector(selectCurrentWorkspace);

  if (!currentWorkspace) {
    return <Navigate to={paths.dashboard.root} replace />;
  }

  return <Navigate to={paths.dashboard.worksheetTest(currentWorkspace.id)} replace />;
}

/**
 * Redirects from /dashboard to the default workspace
 */
export function DashboardWorkspaceRedirect() {
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const workspaces = useSelector(selectWorkspaces);

  // If there's a current workspace, redirect to it
  if (currentWorkspace) {
    return <Navigate to={paths.dashboard.content(currentWorkspace.id, 'root')} replace />;
  }

  // If there are workspaces but no current one selected, redirect to the first one
  if (workspaces.length > 0) {
    return <Navigate to={paths.dashboard.content(workspaces[0].id, 'root')} replace />;
  }

  // No workspaces available, stay on overview
  return <Navigate to={paths.dashboard.overview} replace />;
}
